import { Box, FormGroup, IconButton, Select } from '@ksyos/design-system';
import type { IJournal } from '@common/validation/episodesAndJournal';
import type { IEpisode } from '@frontend/components/interfaces/patientEpisode';
import type { JournalEntries } from '@frontend/components/pages/customControls/utils/v1/episodeJournals/episodeJournals';
import type { ValidationErrors } from '@frontend/utils';
import { JournalEntryType } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { JournalTypeCheckbox } from './JournalTypeCheckbox';

interface IJournalEntryAddEditProps {
    availableEpisodes?: IEpisode[];
    episodes?: IEpisode[];
    errors?: ValidationErrors;
    journalEntries: JournalEntries;
    journalEntry: IJournal;
    journalEntryId: string;
    onChange: (journal: IJournal, journalKey: string) => void;
    onChangeEpisode: (newEpisode: string | null, oldEpisode: string | null) => void;
    onDelete: (value: string) => void;
    variant: 'outline' | 'flushed';
}
export function EpisodeJournalEntryAddEdit(props: IJournalEntryAddEditProps) {
    const {
        availableEpisodes,
        episodes,
        errors,
        journalEntries,
        journalEntry,
        journalEntryId,
        onDelete,
        onChange,
        onChangeEpisode,
        variant,
    } = props;
    const translate = getSelectedLanguage();

    if (!journalEntryId) {
        return null;
    }

    return (
        <>
            <FormGroup
                hasErrors={errors?.has('episodes')}
                label={translate.journals.episodes}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    flexDirection="row"
                    gap={2}
                >
                    <Select
                        onChange={async (option) => {
                            if (option) {
                                onChangeEpisode(option.value, journalEntryId);
                            }
                        }}
                        options={getDropdownOptions()}
                        placeholder={'Selecteer een episode...'}
                        value={
                            journalEntry.episodeValueDisplayName
                                ? {
                                      label: journalEntry.episodeValueDisplayName,
                                      value: getEpisodeIdByName(journalEntry.episodeValueDisplayName),
                                  }
                                : null
                        }
                    />
                    <IconButton
                        icon="delete"
                        onClick={() => onDelete(journalEntryId)}
                    />
                </Box>
            </FormGroup>
            {renderJournalTypeCheckboxes()}
        </>
    );

    function getDropdownOptions() {
        let options = journalEntry.episodeValueDisplayName
            ? new Array({
                  label: journalEntry.episodeValueDisplayName,
                  value: getEpisodeIdByName(journalEntry.episodeValueDisplayName),
              })
            : [];

        const alreadySelectedOptions = journalEntries
            .valueSeq()
            .filter((entry) => entry?.episodeValueDisplayName !== '')
            .map((entry) => entry?.episodeValueDisplayName)
            .toArray();

        const episodesToExclude = alreadySelectedOptions.map((selectedOption) =>
            selectedOption ? getEpisodeIdByName(selectedOption) : null
        );

        if (availableEpisodes) {
            options = [
                ...options.filter((option) => option.value !== ''),
                ...availableEpisodes
                    .filter((episode) => {
                        return (
                            getEpisodeIdByName(journalEntry.episodeValueDisplayName) !== episode.id &&
                            !episodesToExclude.includes(episode.id)
                        );
                    })
                    .map((episode) => ({ label: episode.valueDisplayName, value: episode.id })),
            ];
        }

        return options;
    }

    function getEpisodeIdByName(name: string) {
        if (episodes && name) {
            const selectedEpisode = episodes.find((episode) => episode.valueDisplayName === name);

            return selectedEpisode?.id ?? '';
        }

        return '';
    }

    function renderJournalTypeCheckboxes() {
        return Object.values(JournalEntryType).map((journalType) => {
            return (
                <JournalTypeCheckbox
                    errors={errors}
                    journalEntry={journalEntry}
                    journalType={journalType}
                    key={journalType}
                    onChange={(journal) => onChange(journal, journalEntryId)}
                    variant={variant}
                />
            );
        });
    }
}
